import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { ImageUpload } from "../../../components/ImageUpload";
import { Input } from "../../../components/Input";
import { FontSize, Text } from "../../../components/Text";
import { UpdateUserDto } from "../../../model/user/dto/UpdateUserDto";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { userActions } from "../../../redux/user/user.slice";
import { Breakpoints } from "../../../theme/Breakpoints";
import { Rounded } from "../../../theme/Rounded";
import { Shadow } from "../../../theme/Shadow";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { standardDate } from "../../../utils/Date";
import omitBy from "lodash/omitBy";
import isUndefined from "lodash/isUndefined";

const Section = styled(Flex)`
  flex-direction: column;
  gap: 0.2rem;
`;

const SectionTitle = styled(Text)`
  font-weight: 600;
  color: ${(props) => props.theme.main};
  font-size: ${FontSize.l};
`;

const Container = styled(Flex)`
  background-color: ${(props) => props.theme.white};
  border-radius: ${Rounded.md};
  box-shadow: ${Shadow.SM};
  padding: ${Spacing.m};
  flex-wrap: wrap;
`;

const LeftSideWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: ${Spacing.s};

  @media (min-width: ${Breakpoints.DESKTOP}) {
    width: 33%;
  }
`;

const ImageUploadWrapper = styled(Flex)`
  img {
    border-radius: ${Rounded.circle};
  }
`;

const SaveButton = styled(Button)`
  height: 2.5rem;
  width: 8.5rem;
  margin: ${Spacing.s} 0 0 auto;
  padding: 0 ${Spacing.s};
`;

const StyledInput = styled(Input)`
  width: 20rem;
  border: 1px solid ${(props) => props.theme.darkSub};
`;

export const ProfileEditPersonal: React.FC = () => {
  const [presonalInfoUpdateLoading, setPresonalInfoUpdateLoading] =
    useState(false);

  const { t } = useTranslation();

  const loggedInUser = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const { control, handleSubmit, getValues, setValue } = useForm<
    Omit<UpdateUserDto, "company" | "website">
  >({
    defaultValues: {
      name: loggedInUser.name,
      phone: loggedInUser?.phone,
      picture: loggedInUser?.picture,
    },
  });

  const dispatch = useAppDispatch();

  const joiningDate = standardDate(loggedInUser.createdAt, loggedInUser);

  const updateUserPersonalInfo = async () => {
    try {
      setPresonalInfoUpdateLoading(true);

      const dto = getValues();

      await dispatch(
        userActions.updateUserInfo(
          omitBy(
            {
              name: dto?.name,
              userId: loggedInUser._id,
              picture: dto?.picture,
            },
            isUndefined,
          ) as Omit<UpdateUserDto, "company" | "website">,
        ),
      ).unwrap();
      await dispatch(
        userActions.getLoggedInUser({ userId: loggedInUser._id }),
      ).unwrap();

      AtiraToast.success(t("profile.personal.update.success"));
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setPresonalInfoUpdateLoading(false);
    }
  };

  return (
    <Section marginTop="m">
      <SectionTitle>{t("profile.title.personal_info")}</SectionTitle>

      <Container>
        <LeftSideWrapper>
          <Controller
            control={control}
            name="picture"
            render={({ field: { onChange, value } }) => (
              <ImageUploadWrapper>
                <ImageUpload
                  maxCount={1}
                  onRemove={() => setValue("picture", "")}
                  onChange={onChange}
                  image={value || ""}
                  cropProps={{ aspectRatio: 1, aspectSlider: false }}
                />
              </ImageUploadWrapper>
            )}
          />

          <Text fontSize="s">{`${t("common.since")} ${joiningDate}`}</Text>
        </LeftSideWrapper>

        <Flex flexDirection="column" gap="l" flexGrow={1}>
          <Controller
            name="name"
            control={control}
            render={({ field: { value, onChange } }) => (
              <StyledInput
                value={value}
                onChange={onChange}
                type="text"
                title={t("common.full_name")}
              />
            )}
          />

          <SaveButton
            title={t("common.save_changes").toUpperCase()}
            onClick={handleSubmit(updateUserPersonalInfo)}
            loading={presonalInfoUpdateLoading}
          />
        </Flex>
      </Container>
    </Section>
  );
};
